import {
  Component,
  input,
  output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
  AfterViewInit,
  computed,
  inject,
  effect,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { PaginatorComponent } from 'app/shared/components/paginator/components/paginator/paginator.component';
import {
  TableSortEvent,
  TableChangeEvent,
  TableState,
} from './models/tableEvent.model';
import { DatatableCellAnchorDirective } from './directives/datatable-cell-anchor.directive';
import { BaseDatatableCellComponent } from './base-datatable-cell.component';
import { DatatableHeaderAnchorDirective } from './directives/datatable-header-anchor.directive';
import { BaseDatatableHeaderComponent } from './base-datatable-header.component';
import { ColumnDatatable } from './models/columnDatatable.model';
import { Router } from '@angular/router';
import { TableHeaderCheckboxToggleEvent } from 'primeng/table';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<T> implements AfterViewInit {
  tableData = input.required<T[]>();
  totalRecords = input.required<number>();
  columns = input.required<ColumnDatatable<T>[]>();
  dataKey = input.required<string>();
  paginate = input(false, { transform: booleanAttribute });
  rowsPerPage = input(10);
  showCheckbox = input(false, { transform: booleanAttribute });
  stripedRows = input(false, { transform: booleanAttribute });
  selectItems = output<T[]>();
  tableChange = output<TableChangeEvent>();
  rowClick = output<T>();
  selectedItems = signal<T[]>([]);
  sortState = signal<TableSortEvent>({
    field: undefined,
    order: undefined,
  });
  loading = input(false, { transform: booleanAttribute });
  headerFilter = signal<Record<string, unknown>>({});
  dataSliced = computed(() => {
    return this.tableData().slice(0, this.rowsPerPage());
  });
  router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(
      () => {
        if (this.tableData().length) {
          this.renderCells();
          this.cdr.detectChanges();
        }
      },
      { allowSignalWrites: true },
    );
  }

  @ViewChild('paginator') paginator?: PaginatorComponent;

  @ViewChildren(DatatableCellAnchorDirective)
  cellHosts!: QueryList<DatatableCellAnchorDirective>;

  @ViewChildren(DatatableHeaderAnchorDirective)
  headerHosts!: QueryList<DatatableHeaderAnchorDirective>;

  ngAfterViewInit() {
    this.renderHeaderComponents();
  }

  private onHeaderFilterChange(value: Record<string, unknown>) {
    this.headerFilter.update(prev => ({
      ...prev,
      ...value,
    }));
    this.onTableChange();
  }

  private renderHeaderComponents() {
    this.headerHosts.forEach((headerHost, index) => {
      const columnIndex = index % this.columns().length;
      const columnDef = this.columns()[columnIndex];

      if (!columnDef.headerComponent) return;
      const component =
        columnDef.headerComponent || BaseDatatableHeaderComponent;
      const viewContainerRef = headerHost.viewContainerRef;
      viewContainerRef.clear();

      const componentRef = viewContainerRef.createComponent(component);
      const instance = componentRef.instance as BaseDatatableHeaderComponent;

      instance.colDef.set(columnDef);
      instance.index.set(index);
      instance.onFilterChange.set(this.onHeaderFilterChange.bind(this));
    });
  }

  private renderCells() {
    this.cellHosts.forEach((cellHost, index) => {
      const columnIndex = index % this.columns().length;
      const rowIndex = Math.floor(index / this.columns().length);
      const columnDef = this.columns()[columnIndex];
      const rowData = this.tableData()[rowIndex];
      const component = columnDef.cellComponent || BaseDatatableCellComponent;
      const viewContainerRef = cellHost.viewContainerRef;
      viewContainerRef.clear();

      const componentRef = viewContainerRef.createComponent(component);

      const instance = componentRef.instance as BaseDatatableCellComponent;

      instance.value.set(rowData[columnDef.field]);
      instance.rowData.set(rowData);
      instance.colDef.set(columnDef);
      instance.index.set(index);
    });
  }

  protected onSelectChange() {
    this.selectItems.emit(this.selectedItems());
    this.cdr.detectChanges();
  }

  protected onSort(event: TableSortEvent) {
    this.sortState.set(event);
    this.onTableChange();
  }

  protected onTableChange() {
    this.tableChange.emit(this.tableState);
  }

  get tableState(): TableState {
    return {
      paginatorState: this.paginator?.paginatorState(),
      sortState: this.sortState(),
      headerFilter: this.headerFilter(),
    };
  }

  protected get classToApply() {
    const columns = this.columns();

    const hasColSpan = columns.some(col => col.colSpan);

    const container = () => ({
      grid: hasColSpan,
      'grid-cols-12': hasColSpan,
    });

    const item = (colIndex: number) => {
      const col = columns[colIndex];

      const itemClass = {
        'col-span-1': col.colSpan == 1,
        'col-span-2': col.colSpan == 2,
        'col-span-3': col.colSpan == 3,
        'col-span-4': col.colSpan == 4,
        'col-span-5': col.colSpan == 5,
        'col-span-6': col.colSpan == 6,
        'col-span-7': col.colSpan == 7,
        'col-span-8': col.colSpan == 8,
        'col-span-9': col.colSpan == 9,
        'col-span-10': col.colSpan == 10,
        'col-span-11': col.colSpan == 11,
        'col-span-12': col.colSpan == 12,
      };

      return itemClass;
    };

    return {
      container,
      item,
    };
  }

  protected onHeaderCheckboxToggle(event: TableHeaderCheckboxToggleEvent) {
    const key = this.dataKey() as keyof T;
    const selectedKeys = this.selectedItems().map(item => item[key]);
    const dataSlicedKeys = this.dataSliced().map(item => item[key]);

    let listUpdated: T[] = [];

    if (event.checked) {
      const itemToAdd = this.dataSliced().filter(
        item => !selectedKeys.includes(item[key]),
      );

      this.selectedItems().push(...itemToAdd);
      listUpdated = this.selectedItems();
    } else {
      listUpdated = this.selectedItems().filter(
        item => !dataSlicedKeys.includes(item[key]),
      );
    }

    this.selectedItems.set(listUpdated);
    this.onSelectChange();
  }

  protected onRowClicked(rowData: T) {
    this.rowClick.emit(rowData);
  }
}
